import styles from "./PlannyAccessSection.module.scss";
import { Icon, IconType } from "../../../../common/icons/Icon";
import { RenderWhen } from "../../../../common/infrastructure/components/RenderWhen";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { routePaths } from "../../../../common/infrastructure/routes/routePaths";
import { usePlannyAccessSection } from "../hooks/usePlannyAccessSection";
import { useScrollReveal } from "../../../../common/hooks/useScrollReveal";

export const PlannyAccessSection = () => {
  const {
    isLoading,
    hasLoadFailed,
    isEmpty,
    openCountLabel,
    openCountCaption,
    changeCountLabel,
    changeCountCaption,
    lastActivityLabel,
    groups,
    totalEntries,
  } = usePlannyAccessSection();
  const showStats = !isLoading && !hasLoadFailed;
  // Cards fade-and-rise as they scroll into view, like the studio/user history timelines.
  const timelineRef = useScrollReveal<HTMLDivElement>(totalEntries);

  return (
    <div className={styles.root} ref={timelineRef}>
      <div className={styles.hero}>
        <div className={styles.hero_head}>
          <div className={styles.hero_emblem}>
            <Icon type={IconType.Shield} color="currentColor" width="28px" height="28px" />
          </div>
          <div className={styles.hero_text}>
            <span className={styles.hero_kicker}>Прозрачност</span>
            <h2 className={styles.hero_title}>Достъп от екипа на Planny</h2>
          </div>
        </div>
        <p className={styles.hero_description}>
          Понякога член на екипа на Planny отваря вашия обект или променя настройките му,
          например за да помогне с настройка или проблем. Всяко отваряне се записва
          автоматично тук.
        </p>
        <p className={styles.hero_description}>
          При въпроси, моля свържете се с нас в{" "}
          <Link className={styles.hero_link} to={routePaths.support}>
            секцията за поддръжка
          </Link>
          .
        </p>
        <div className={styles.stats}>
          <div className={styles.stat}>
            <span className={styles.stat_value}>{showStats ? openCountLabel : "…"}</span>
            <span className={styles.stat_label}>{openCountCaption}</span>
          </div>
          <div className={classNames(styles.stat, styles.stat_change)}>
            <span className={styles.stat_value}>{showStats ? changeCountLabel : "…"}</span>
            <span className={styles.stat_label}>{changeCountCaption}</span>
          </div>
          <div className={classNames(styles.stat, styles.stat_lastActivity)}>
            <span className={styles.stat_value}>{showStats ? lastActivityLabel : "…"}</span>
            <span className={styles.stat_label}>последна активност</span>
          </div>
        </div>
      </div>

      <RenderWhen condition={isLoading}>
        <div className={styles.skeleton}>
          <span className={styles.skeleton_row} />
          <span className={styles.skeleton_row} />
          <span className={styles.skeleton_row} />
        </div>
      </RenderWhen>

      <RenderWhen condition={hasLoadFailed}>
        <div className={classNames(styles.empty, styles.empty_error)}>
          <span className={styles.empty_title}>Историята на достъпите не можа да се зареди</span>
          <span className={styles.empty_text}>Опитайте отново след малко.</span>
        </div>
      </RenderWhen>

      <RenderWhen condition={isEmpty}>
        <div className={styles.empty}>
          <div className={styles.empty_emblem}>
            <Icon type={IconType.Shield} color="currentColor" width="30px" height="30px" />
          </div>
          <span className={styles.empty_title}>Няма записани действия</span>
          <span className={styles.empty_text}>
            Никой от екипа на Planny не е отварял или променял вашите обекти.
          </span>
        </div>
      </RenderWhen>

      {groups.map((group) => (
        <section key={group.key} className={styles.month}>
          <h3 className={styles.month_label}>{group.label}</h3>
          <ol className={styles.timeline}>
            {group.entries.map((entry) => (
              <li
                key={entry.id}
                className={classNames(styles.entry, {
                  [styles.entry_change]: entry.isChange,
                  [styles.entry_exit]: entry.isExit,
                })}
              >
                <span className={styles.entry_node} aria-hidden />
                <div className={styles.entry_card} data-reveal>
                  <div className={styles.entry_top}>
                    <span className={styles.entry_actor}>{entry.actorLabel}</span>
                    <span className={styles.entry_relative}>{entry.relative}</span>
                  </div>
                  <span className={styles.entry_action}>{entry.actionLabel}</span>
                  <RenderWhen condition={!!entry.changedFieldsLabel}>
                    <span className={styles.entry_details}>{entry.changedFieldsLabel}</span>
                  </RenderWhen>
                  <RenderWhen condition={!!entry.reasonLabel}>
                    <span className={styles.entry_reason}>{entry.reasonLabel}</span>
                  </RenderWhen>
                  <div className={styles.entry_bottom}>
                    <span className={styles.entry_studio}>{entry.studioName}</span>
                    <span className={styles.entry_time}>
                      {entry.date} · {entry.time}
                    </span>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
};
